'use client'
import { CityShippingMethodsListQuery } from '@/graphql'
import { useTranslations } from 'next-intl'
import { useEffect, useId, useMemo, useState } from 'react'
import Select, { SingleValue } from 'react-select'
import { formatOptionLabel, selectComponent, selectStyles } from './component'

interface CustomSelectProps {
    time?: CityShippingMethodsListQuery | undefined
    setShippingStartTime: (time: string) => void
    shippingStartTime: string
    shippingEndTime: string
    setShippingEndTime: (time: string) => void
    shippingDate: string | null
    cityShippingMethod: string | null
}

interface TimeRange {
  endTime: string
  startTime: string
}

export interface OptionType {
    value: string;
    label: string;
    isDisabled: boolean;
    startTime: string;
    endTime: string;
    cityShippingMethod: string
}

const formatDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

const CustomSelect = ({
    time,
    setShippingStartTime,
    setShippingEndTime,
    shippingStartTime,
    shippingEndTime,
    shippingDate,
    cityShippingMethod
}: CustomSelectProps) => {
  const timeRanges =
    time?.cityShippingMethods?.edges?.[0]?.node?.timeRanges || []
  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null)

  const deliveryOptions = useMemo((): OptionType[] => {
    const now = new Date()
    const todayDateStr = formatDate(now)

    const shouldCheckTime = shippingDate === todayDateStr

    let currentTotalMinutes = 0
    if (shouldCheckTime) {
      const currentHour = now.getHours()
      const currentMinute = now.getMinutes()
      currentTotalMinutes = currentHour * 60 + currentMinute
    }

    return timeRanges.map((timeRange: TimeRange) => {
      let isDisabled = false

      if (shouldCheckTime) {
        const [endHourStr, endMinuteStr] = timeRange.endTime.split(':')
        const endHour = parseInt(endHourStr, 10)
        const endMinute = parseInt(endMinuteStr, 10)
        const endTotalMinutes = endHour * 60 + endMinute

        isDisabled = currentTotalMinutes >= endTotalMinutes
      }

            return {
                value: `${timeRange.startTime}-${timeRange.endTime}`,
                label: `${timeRange.startTime.slice(0, 5)} - ${timeRange.endTime.slice(0, 5)}`,
                isDisabled: isDisabled,
                startTime: timeRange.startTime,
                endTime: timeRange.endTime,
                cityShippingMethod: cityShippingMethod
            };
        });
    }, [timeRanges, shippingDate]);

  const handleChange = (newValue: SingleValue<OptionType>) => {
    setSelectedOption(newValue)
    if (newValue) {
      setShippingStartTime(newValue.startTime)
      setShippingEndTime(newValue.endTime)
    } else {
      setShippingStartTime('')
      setShippingEndTime('')
    }
  }

  useEffect(() => {
    if (!shippingDate) {
      if (selectedOption !== null) {
        setSelectedOption(null)
        setShippingStartTime('')
        setShippingEndTime('')
      }
      return
    }
  }, [deliveryOptions, shippingDate, selectedOption])

  useEffect(() => {
    setSelectedOption(null)
    if (shippingStartTime) setShippingStartTime('')
    if (shippingEndTime) setShippingEndTime('')
  }, [shippingDate])

  useEffect(() => {
    if (!shippingDate) {
      if (selectedOption !== null) setSelectedOption(null)
      return
    }

    const hasTimeProps = shippingStartTime && shippingEndTime
    let foundOptionFromProps: OptionType | undefined = undefined
    if (hasTimeProps) {
      const targetStartTime = shippingStartTime.slice(0, 5)
      const targetEndTime = shippingEndTime.slice(0, 5)

      foundOptionFromProps = deliveryOptions.find(
        (option) =>
          option.startTime.slice(0, 5) === targetStartTime &&
          option.endTime.slice(0, 5) === targetEndTime &&
          !option.isDisabled
      )
    }

    if (foundOptionFromProps) {
      if (selectedOption?.value !== foundOptionFromProps.value) {
        setSelectedOption(foundOptionFromProps)
        setShippingStartTime(foundOptionFromProps.startTime)
        setShippingEndTime(foundOptionFromProps.endTime)
      }
    }
  }, [
    shippingStartTime,
    shippingEndTime,
    setShippingStartTime,
    setShippingEndTime,
  ])
  const t = useTranslations('Components.custom-select')
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Select<OptionType>
        instanceId={useId()}
        value={selectedOption}
        isSearchable={false}
        styles={selectStyles}
        options={deliveryOptions}
        onChange={handleChange}
        components={selectComponent}
        formatOptionLabel={formatOptionLabel}
        isOptionDisabled={(option) => option.isDisabled}
        menuPosition="fixed"
        menuPlacement="bottom"
        placeholder={shippingDate ? t('choose-time') : t('select-date')}
        isDisabled={!shippingDate}
      />
    </div>
  )
}

export default CustomSelect
